// React
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// Store
import useAuthStore from '../../../store/authStore.js';

// Components
import { Box, Button, Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { 
    VisibilityOffOutlined, VisibilityOutlined, PersonOutline, 
    EmailOutlined, PhoneOutlined, CakeOutlined, LockOutlined,
    ArrowForward
} from '@mui/icons-material';
import LoadingProgress from '../../custom/LoadingProgress.js';

// Style
import { red } from '@mui/material/colors/index.js';
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Page
import RegisterNotConfirmed from './RegisterNotConfirmed.js';

// Other
import { capitalizeString } from '../../../utils/index.js';
import { confirmInvitationLink } from '../../../service/httpService/invitationLink/index.js';

function RegisterForm({ showError }) {
	const [searchParams] = useSearchParams();
	const { colors } = useAppTheme();

	// Form
	const [email, setEmail] = useState('');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [telephone, setTelephone] = useState('');
	const [dateOfBirth, setDateOfBirth] = useState(new Date().toISOString().split('T')[0]);

	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setLoading] = useState(true);
	const [isConfirmed, setConfirmed] = useState(false);
	const [invitationLink, setInvitationLink] = useState(false);
    const [focusedField, setFocusedField] = useState(null);
    const emailInputRef = useRef(null);
    const passwordInputRef = useRef(null);
    const confirmPasswordInputRef = useRef(null);

	const navigate = useNavigate();

	const handleMouseDownPassword = (event) => {
        if (event.button === 0) event.preventDefault();
	}

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	}

	const handleKeyDown = (event) => {
		if (event.key === 'Enter') {
			doRegister();
		}
	}

	const onChangeFirstName = (event) => {
		if (!/\d/.test(event.target.value)) {
			setFirstName(capitalizeString(event.target.value));
		}
	}

	const onChangeLastName = (event) => {
		if (!/\d/.test(event.target.value)) {
			setLastName(capitalizeString(event.target.value));
		}
	}

	const onChangeDateOfBirth = (event) => {
		if (event.target.value.length <= 10) {
			setDateOfBirth(event.target.value);
		}
	}

	const onChangeEmail = (event) => {
		setEmail(String(event.target.value).toLocaleLowerCase());
	}

    // Sync autofilled values (password managers) into React state
    useEffect(() => {
        const syncFromDom = () => {
            const domEmail = emailInputRef.current?.value;
            const domPassword = passwordInputRef.current?.value;
            const domConfirm = confirmPasswordInputRef.current?.value;

            if (typeof domEmail === 'string' && domEmail !== '' && domEmail !== email) {
                setEmail(domEmail.toLocaleLowerCase());
            }
            if (typeof domPassword === 'string' && domPassword !== '' && domPassword !== password) {
                setPassword(domPassword);
            }
            if (typeof domConfirm === 'string' && domConfirm !== '' && domConfirm !== confirmPassword) {
                setConfirmPassword(domConfirm);
            }
        };

        let tries = 0;
        syncFromDom();
        const id = setInterval(() => {
            syncFromDom();
            tries += 1;
            if (tries >= 10) clearInterval(id);
        }, 200);
        return () => clearInterval(id);
    }, [email, password, confirmPassword]);
	
	useEffect(() => {
		confirmInvitationLink(searchParams.get('invitationLink'))
		.then(response => {
			setLoading(false);
			if(response.data.confirm) {
				setConfirmed(true);
				setInvitationLink(searchParams.get('invitationLink'));
                if (response.data.email) {
                    setEmail(response.data.email);
                }
				return;
			} 
			setConfirmed(false);
		})
		.catch(() => {
			showError('Fehler beim Laden der Seite!');
		});
	}, [searchParams, showError]);

	const { register } = useAuthStore();
	const doRegister = () => {
        if (firstName.trim() === '') {
            showError('Vorname ist ein Pflichtfeld.');
            return;
        }
		if (lastName.trim() === '') {
            showError('Nachname ist ein Pflichtfeld.');
            return;
        }
        if (telephone.trim() === '') {
            showError('Telefonnummer ist ein Pflichtfeld.');
            return;
        }
        if (password === '') {
            showError('Passwort ist ein Pflichtfeld.');
            return;
        }
		if(!dateOfBirth && email !== '' && telephone !== '' && lastName !== '' && firstName !== '') {
			showError('Eingabefehler: Bitte alle Felder überprüfen.');
			return;
		}

		if(password !== confirmPassword) {
			showError('Passwörter stimmen nicht überein');
			return;
		}
		if(password.length < 8) {
			showError('Passwort zu kurz (min. 8 Zeichen)');
			return;
		}

		register({ email, firstName, lastName, password, telephone, dateOfBirth, invitationLink }).then(res => {
			if(res.error) {
				showError(res.message);
				return;
			}
			navigate('/');
		});
	}

    // Shared styling for input fields to match Login design (light mode)
    const inputStyle = (fieldName, hasError = false) => ({
        '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            backgroundColor: colors.login.input.bg,
            '& fieldset': {
                borderColor: hasError ? red[600] : (focusedField === fieldName ? colors.login.input.borderFocus : colors.login.input.border),
            },
            '&:hover fieldset': {
                borderColor: hasError ? red[600] : (focusedField === fieldName ? colors.login.input.borderFocus : colors.login.input.borderHover),
            },
            '&.Mui-focused fieldset': {
                borderColor: hasError ? red[600] : colors.login.input.borderFocus,
            },
            // Match Login autofill look (avoid Chrome yellow)
            '&:has(input:-webkit-autofill)': {
                backgroundColor: colors.login.input.bg,
            },
            '&:has(input:-webkit-autofill:hover)': {
                backgroundColor: colors.login.input.bgHover,
            },
            '&:has(input:-webkit-autofill:focus)': {
                backgroundColor: colors.login.input.bgFocus,
            },
            // Disabled field styling - readable but visibly grayed out
            '&.Mui-disabled': {
                opacity: 0.7,
                '& input': {
                    WebkitTextFillColor: `${colors.login.input.text} !important`,
                    color: `${colors.login.input.text} !important`,
                },
            },
        }
    });

	const autofillInputSx = {
		'& input:-webkit-autofill': {
			WebkitBoxShadow: '0 0 0 100px transparent inset !important',
			WebkitTextFillColor: 'inherit !important',
			transition: 'background-color 5000s ease-in-out 0s',
		},
	};

	// Label props that match Login page behavior
	const getLabelProps = (fieldName, fieldValue, hasError = false) => ({
		sx: {
			color: hasError ? red[600] : (fieldValue ? colors.login.input.text : colors.login.input.label),
			'&.Mui-focused': {
				color: hasError ? `${red[600]} !important` : `${colors.login.input.borderFocus} !important`,
			},
			'&.Mui-error': {
				color: `${red[600]} !important`,
			}
		}
	});

	// Icon and text color for input props
	const getInputProps = (startIcon, endAdornment = null) => ({
		startAdornment: startIcon ? React.cloneElement(startIcon, { sx: { mr: 1, color: colors.login.input.icon } }) : null,
		endAdornment,
		sx: {
			color: colors.login.input.text,
		}
	});

	if (isLoading) { 
		return (<LoadingProgress/>);
	}

	if (!isConfirmed) {
		return (<RegisterNotConfirmed showError={ showError }/>);
	}

	return (
		<Box>
			<Grid container spacing={2}>
				<Grid item xs={6}>
					<TextField 
                        fullWidth 
                        required 
                        value={ firstName } 
                        onChange={ onChangeFirstName } 
                        variant="outlined" 
                        label="Vorname"
                        onKeyDown={ handleKeyDown }
                        onFocus={() => setFocusedField('firstName')}
                        onBlur={() => setFocusedField(null)}
                        InputLabelProps={getLabelProps('firstName', firstName)}
                        InputProps={getInputProps(<PersonOutline />)}
                        sx={inputStyle('firstName')}
                    />
				</Grid>

				<Grid item xs={6}>
					<TextField 
                        fullWidth 
                        required 
                        value={ lastName } 
                        onChange={ onChangeLastName } 
                        variant="outlined" 
                        label="Nachname"
                        onKeyDown={ handleKeyDown }
                        onFocus={() => setFocusedField('lastName')}
                        onBlur={() => setFocusedField(null)}
                        InputLabelProps={getLabelProps('lastName', lastName)}
                        InputProps={getInputProps(<PersonOutline />)}
                        sx={inputStyle('lastName')}
                    />
				</Grid>

				<Grid item xs={6}>
					<TextField 
                        fullWidth 
                        disabled 
                        value={ email } 
                        onChange={ onChangeEmail } 
                        variant="outlined" 
                        type="email" 
                        label="E-Mail"
						name="email"
						autoComplete="email"
                        onKeyDown={ handleKeyDown }
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        inputRef={ emailInputRef }
                        InputLabelProps={getLabelProps('email', email)}
                        InputProps={getInputProps(<EmailOutlined />)}
                        sx={{ ...inputStyle('email'), ...autofillInputSx }}
                    />
				</Grid>

				<Grid item xs={6}>
					<TextField 
                        fullWidth 
                        required 
                        label="Geburtsdatum" 
                        value={ dateOfBirth } 
                        onChange={ onChangeDateOfBirth } 
                        variant="outlined" 
                        type="date"
                        onKeyDown={ handleKeyDown }
                        onFocus={() => setFocusedField('dateOfBirth')}
                        onBlur={() => setFocusedField(null)}
                        InputLabelProps={getLabelProps('dateOfBirth', dateOfBirth)}
                        InputProps={getInputProps(<CakeOutlined />)}
                        sx={inputStyle('dateOfBirth')}
                    />
				</Grid>

				<Grid item xs={12}>
					<TextField 
                        fullWidth 
                        required 
                        value={ telephone } 
                        onChange={ (event) => { setTelephone(event.target.value) }} 
                        variant="outlined" 
                        label="Telefonnummer" 
                        type="text"
                        onKeyDown={ handleKeyDown }
                        onFocus={() => setFocusedField('telephone')}
                        onBlur={() => setFocusedField(null)}
                        InputLabelProps={getLabelProps('telephone', telephone)}
                        InputProps={getInputProps(<PhoneOutlined />)}
                        sx={inputStyle('telephone')}
                    />
				</Grid>

				<Grid item xs={12}>
					<TextField 
                        fullWidth 
                        required 
                        value={ password } 
                        onChange={ (event) => setPassword(event.target.value) } 
                        variant="outlined" 
                        label="Passwort" 
                        type={ showPassword ? 'text' : 'password' }
						name="newPassword"
						autoComplete="new-password"
                        onKeyDown={ handleKeyDown }
                        onFocus={() => setFocusedField('password')}
                        onBlur={() => setFocusedField(null)}
                        inputRef={ passwordInputRef }
                        InputLabelProps={getLabelProps('password', password)}
                        InputProps={getInputProps(<LockOutlined />, (
                            <InputAdornment position="end">
                                <IconButton 
                                    onClick={ handleClickShowPassword } 
                                    onMouseDown={ handleMouseDownPassword } 
                                    edge="end"
                                    sx={{ color: colors.login.input.icon }}
                                >
                                    { showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined /> }
                                </IconButton>
                            </InputAdornment>
                        ))}
                        sx={{ ...inputStyle('password'), ...autofillInputSx }}
                    />
				</Grid>

				<Grid item xs={12}>
					<TextField 
                        fullWidth 
                        required 
                        value={ confirmPassword } 
                        onChange={ (event) => setConfirmPassword(event.target.value) } 
                        variant="outlined" 
                        label="Passwort bestätigen" 
                        type={ showPassword ? 'text' : 'password' }
						name="confirmPassword"
						autoComplete="new-password"
                        onKeyDown={ handleKeyDown }
                        onFocus={() => setFocusedField('confirmPassword')}
                        onBlur={() => setFocusedField(null)}
                        inputRef={ confirmPasswordInputRef }
                        InputLabelProps={getLabelProps('confirmPassword', confirmPassword)}
                        InputProps={getInputProps(<LockOutlined />, (
                            <InputAdornment position="end">
                                <IconButton 
                                    onClick={ handleClickShowPassword } 
                                    onMouseDown={ handleMouseDownPassword } 
                                    edge="end"
                                    sx={{ color: colors.login.input.icon }}
                                >
                                    { showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined /> }
                                </IconButton>
                            </InputAdornment>
                        ))}
                        sx={{ ...inputStyle('confirmPassword'), ...autofillInputSx }}
                    />
				</Grid>
			</Grid>

            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                    variant="contained" 
                    onClick={ doRegister } 
                    size="large"
                    endIcon={<ArrowForward />}
                    sx={{ 
                        width: '100%', 
                        color: 'white', 
                        backgroundColor: colors.login.button.bg, 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 700,
                        py: 1.5,
                        boxShadow: colors.login.button.shadow,
                        '&:hover': { 
                            backgroundColor: colors.login.button.hover, 
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                            transform: 'translateY(-1px)'
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        },
                        transition: 'all 0.3s ease'
                    }} 
                >
                    Registrierung abschließen
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography variant='body2' sx={{ color: colors.login.input.label }}>
                        Bereits registriert?&nbsp;
                    </Typography>
                    <Link to="/" style={{ textDecoration: 'none' }}>
                        <Typography variant='body2' fontWeight={600} sx={{ 
                            color: colors.login.link.text,
                            '&:hover': {
                                color: colors.login.link.hover,
                            }
                        }}>
                            Zur Anmeldung
                        </Typography>
                    </Link>
                </Box>
            </Box>
		</Box>
	);
}

export default RegisterForm;